/**
 * Single source of truth for pfh-mobile navigation (IOS-6).
 * Presentational nav components (TabBar, SubNavPills, MoreSheet) consume these
 * arrays. To move/add an entry, edit this file only — never touch a component.
 */
import type { LucideIcon } from 'lucide-react'
import { isPathHidden, type Flavor } from '../flavor/flavorConfig.ts'
import {
  LayoutDashboard, Wallet, TrendingUp, MoreHorizontal,
  User, Settings, ShieldCheck, Users, Globe,
  Receipt, GitCompare, Upload, FileText,
} from 'lucide-react'

export interface TabItem {
  labelKey: string
  to: string
  icon: LucideIcon
  /** When true, NavLink uses `end` so only the exact path is active. */
  end?: boolean
  /** When set, tapping opens the MoreSheet instead of navigating. */
  opensMore?: boolean
}

export interface SubNavItem {
  labelKey: string
  to: string
}

export interface MoreItem {
  labelKey: string
  to: string
  icon: LucideIcon
}

export const TABS: TabItem[] = [
  { labelKey: 'mobile.nav.home', to: '/', icon: LayoutDashboard, end: true },
  { labelKey: 'mobile.nav.accounts', to: '/accounts', icon: Wallet },
  { labelKey: 'mobile.nav.portfolio', to: '/portfolio', icon: TrendingUp },
  { labelKey: 'mobile.nav.more', to: '#more', icon: MoreHorizontal, opensMore: true },
]

export const PAZURU_SUBNAV: SubNavItem[] = [
  { labelKey: 'mobile.nav.accounts', to: '/accounts' },
  { labelKey: 'mobile.nav.transactions', to: '/transactions' },
  { labelKey: 'mobile.nav.recurring', to: '/recurring' },
  { labelKey: 'mobile.nav.spending', to: '/spending' },
  { labelKey: 'mobile.nav.forecasts', to: '/forecasts' },
  { labelKey: 'mobile.nav.budgets', to: '/budgets' },
  { labelKey: 'mobile.nav.goals', to: '/goals' },
  { labelKey: 'mobile.nav.debts', to: '/debts' },
  { labelKey: 'mobile.nav.jeliLab', to: '/pazuru/lab' },
  { labelKey: 'mobile.nav.importLog', to: '/pazuru/import-log' },
]

export const PORTFOLIO_SUBNAV: SubNavItem[] = [
  { labelKey: 'mobile.nav.portfolio', to: '/portfolio' },
  { labelKey: 'mobile.nav.etf', to: '/etf' },
  { labelKey: 'mobile.nav.crypto', to: '/crypto' },
  { labelKey: 'mobile.nav.analysis', to: '/analysis' },
  { labelKey: 'mobile.nav.backtest', to: '/backtest' },
  { labelKey: 'mobile.nav.screener', to: '/screener' },
  { labelKey: 'mobile.nav.compare', to: '/compare' },
  { labelKey: 'mobile.nav.news', to: '/sentiment' },
  { labelKey: 'mobile.nav.jeliLab', to: '/tsumi/lab' },
]

export const MORE_ITEMS: MoreItem[] = [
  { labelKey: 'mobile.nav.profile', to: '/foroba/profile', icon: User },
  { labelKey: 'mobile.nav.settings', to: '/foroba/settings', icon: Settings },
  { labelKey: 'mobile.nav.security', to: '/foroba/security', icon: ShieldCheck },
  { labelKey: 'mobile.nav.household', to: '/foroba/household', icon: Users },
  { labelKey: 'mobile.nav.markets', to: '/markets', icon: Globe },
  { labelKey: 'mobile.nav.tax', to: '/tax', icon: Receipt },
  { labelKey: 'mobile.nav.whatIf', to: '/simulate', icon: GitCompare },
  { labelKey: 'mobile.nav.import', to: '/import', icon: Upload },
  { labelKey: 'mobile.nav.legal', to: '/legal', icon: FileText },
]

/** Tabs/More entries visible in a build flavour (BR-14). `#more` is never hidden. */
export const tabsFor = (flavor: Flavor): TabItem[] => TABS.filter((t) => !isPathHidden(flavor, t.to))
export const moreItemsFor = (flavor: Flavor): MoreItem[] => MORE_ITEMS.filter((m) => !isPathHidden(flavor, m.to))
